import { Box, DialogActions, DialogContent, Modal, ModalDialog, Sheet, Stack, Typography } from '@mui/joy';
import { addDays, getDate, getMonth, getYear } from 'date-fns';
import React, { FC } from 'react';

import { useFetchOrganizationOfCurrentUser } from '@MP/shared/api/hooks/auth';
import { useCreateDelivery } from '@MP/shared/api/hooks/delivery';
import { ScanMiniSensorType, useCreatePrescription } from '@MP/shared/api/hooks/scanMini';
import { RectangleButton } from '@MP/shared/components/Button';
import { useMinddSnackbar } from '@MP/shared/components/MinddSnackbarProvider';
import ScanMiniRoundIcon from '@MP/shared/icon/ScanMiniRoundIcon';
import { selectUser, usePatientStore } from '@MP/shared/providers/PatientProvider';

import CancelConfirmDialog from '../CancelConfirmDialog';
import DateSelector from '../DateSelector';
import MeasurementCountSelector from '../MeasurementCountSelector';
import SensorTypeSelector from '../SensorTypeSelector';

const defaultDate = addDays(new Date(), 5);

const NewScanMiniScheduleDialog: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [isLoading, setLoading] = React.useState(false);
  const [day, setDay] = React.useState<number | undefined>(getDate(defaultDate));
  const [measurementCount, setMeasurementCount] = React.useState<number | null>(null);
  const [selectedSensorType, setSelectedSensorType] = React.useState<ScanMiniSensorType | null>(null);
  const [openClose, setOpenClose] = React.useState(false);

  const snackbar = useMinddSnackbar();
  const user = usePatientStore(selectUser);
  const { data: org } = useFetchOrganizationOfCurrentUser();
  const { mutateAsync: createPrescription } = useCreatePrescription();
  const { mutateAsync: createDelivery } = useCreateDelivery();
  // 폼 유효성 측정
  const isFormValid = day !== undefined && measurementCount !== null && selectedSensorType !== null;

  const handleSubmit = async () => {
    if (
      !user ||
      !org ||
      !isFormValid ||
      day === undefined ||
      measurementCount === null ||
      selectedSensorType === null
    ) {
      return;
    }

    setLoading(true);

    try {
      const prescription = await createPrescription({
        patientId: user.id,
        measurementCount: measurementCount,
        sensorType: selectedSensorType,
      });
      // await createDelivery({
      //   orgId: org.id!,
      //   orgName: org.name!,
      //   prescriptionId: prescription.id!,
      //   patientId: store.user.id,
      //   rentalDays: measurementCount,
      // });
      // snackbar.add('측정 정보 등록이 완료되어 환자에게 배송 요청 링크가 발송되었습니다.');
      snackbar.add('배송 요청 구현해야함');
      onClose();
    } catch (error) {
      snackbar.add('측정 정보 등록에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setOpenClose(true);
  };

  const handleCloseConfirm = () => {
    onClose();
  };

  const handleCloseCancel = () => {
    setOpenClose(false);
  };

  return (
    <>
      <CancelConfirmDialog open={openClose} onConfirm={handleCloseConfirm} onCancel={handleCloseCancel} />

      <Modal open>
        <ModalDialog sx={{ p: '16px' }}>
          <DialogContent sx={{ width: 440 }}>
            <Stack alignItems="center" spacing="16px" py="24px">
              <ScanMiniRoundIcon width={48} />
              <Typography level="h4">측정 정보 등록</Typography>
            </Stack>
            <Box width="100%" pb="8px">
              <Sheet sx={{ py: '24px', px: '16px' }}>
                <Stack spacing="20px">
                  <MeasurementCountSelector value={measurementCount} onChange={setMeasurementCount} />
                  <SensorTypeSelector value={selectedSensorType} onChange={setSelectedSensorType} />
                </Stack>
              </Sheet>
            </Box>
          </DialogContent>

          <DialogActions>
            <RectangleButton
              loading={isLoading}
              disabled={!isFormValid}
              sx={{ flexGrow: 1, flexBasis: 0 }}
              onClick={handleSubmit}>
              등록
            </RectangleButton>
            <RectangleButton
              color="neutral"
              variant="outlined"
              sx={{ flexGrow: 1, flexBasis: 0 }}
              onClick={handleCancel}>
              취소
            </RectangleButton>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default NewScanMiniScheduleDialog;
